import React from 'react';
import { cn } from '../../utils/cn';
import { CircleX} from 'lucide-react';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void; // Function to handle right icon click
  showValidationError?: boolean; // Flag to enable real-time validation
  showRemoveIcon?: boolean; // Flag to show remove icon for file inputs
  readOnly?: boolean; // Flag to make input read-only
  showAsterik?: boolean; // Flag to show asterik for required fields
  blockSpaces?: boolean; // Flag to prevent spaces in input
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  helpText,
  leftIcon,
  rightIcon,
  onRightIconClick,
  showValidationError = false,
  showRemoveIcon = false,
  className,
  id,
  readOnly,
  showAsterik,
  blockSpaces = false,
  ...props
}) => {
  const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
  const [validationError, setValidationError] = React.useState<string>('');
  const [inputValue, setInputValue] = React.useState<string>(props.value as string || '');
  
  // URL validation regex
  const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Phone validation regex (basic) - allows optional +, then 7-15 digits, optional spaces/dashes
  const phoneRegex = /^[+]?[\d\s\-()]{7,20}$/;

  // Update internal state when props.value changes
  React.useEffect(() => {
    setInputValue(props.value as string || '');
  }, [props.value]);

  // Validate input based on type
  const validateInput = (value: string, type: string) => {
    if (!showValidationError || !value.trim()) {
      return '';
    }

    switch (type) {
      case 'url':
        return urlRegex.test(value) ? '' : 'Please enter a valid URL';
      case 'email':
        return emailRegex.test(value) ? '' : 'Please enter a valid email address';
      case 'tel':
      case 'phone':
        return phoneRegex.test(value) ? '' : 'Please enter a valid phone number';
      default:
        return '';
    }
  };

  // Handle input change with validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, type } = e.target;

    // Block all spaces if blockSpaces is true
    if (blockSpaces && value.includes(' ')) {
      return;
    }

    // Prevent spaces at the beginning of input
    if (inputValue === '' && value === ' ') {
      return;
    }

    // Handle number input validation with cross-browser support
    if (type === 'number') {
      // Check if value is empty or just a minus sign (for negative numbers)
      if (value === '' || value === '-') {
        setInputValue(value);
        if (props.onChange) {
          props.onChange(e);
        }
        return;
      }

      // Check if value is a valid number (handles Firefox and other browsers)
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        // Prevent invalid input
        return;
      }

      const { min, max } = props;

      // Check if the value violates min constraint
      if (min !== undefined && numValue < parseFloat(String(min))) {
        return;
      }

      // Check if the value violates max constraint
      if (max !== undefined && numValue > parseFloat(String(max))) {
        return;
      }
    }

    // Update internal state for remove icon visibility
    setInputValue(value);

    // Set validation error based on input type
    if (showValidationError && type !== 'number') {
      const error = validateInput(value, type);
      setValidationError(error);
    }

    // Call the original onChange if validation passes
    if (props.onChange) {
      props.onChange(e);
    }
  };

  // Handle keydown for number and phone inputs to prevent invalid characters
  const handleNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { min, pattern } = props;

    // Handle pattern-based validation (e.g., alphabetic only)
    if (pattern && props.type === 'text') {
      // Allow: backspace, delete, tab, escape, enter, arrows
      if (
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'Tab' ||
        e.key === 'Escape' ||
        e.key === 'Enter' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // Check if key matches the pattern
      // For [A-Za-z]* pattern, only allow letters
      if (pattern === '[A-Za-z]*' || pattern === '[A-Za-z ]*') {
        if (!/^[a-zA-Z\s]$/.test(e.key)) {
          e.preventDefault();
          return;
        }
      }

      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
      return;
    }

    // Handle phone input validation
    if (props.type === 'tel' || props.type === 'phone') {
      // Allow: backspace, delete, tab, escape, enter, arrows
      if (
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'Tab' ||
        e.key === 'Escape' ||
        e.key === 'Enter' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // Allow: numbers, +, -, space, parentheses
      if (!/^[0-9+\-\s()]$/.test(e.key)) {
        e.preventDefault();
        return;
      }

      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
      return;
    }

    if (props.type === 'number') {
      const currentValue = (e.target as HTMLInputElement).value;
      
      // Allow: backspace, delete, tab, escape, enter
      if (
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'Tab' ||
        e.key === 'Escape' ||
        e.key === 'Enter'
      ) {
        // Call the original onKeyDown if provided
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        // Call the original onKeyDown if provided
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // Allow: home, end, left, right, up, down
      if (
        e.key === 'Home' ||
        e.key === 'End' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown'
      ) {
        // Call the original onKeyDown if provided
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // If min is 0 or positive, prevent typing minus sign
      if (min !== undefined && parseFloat(String(min)) >= 0) {
        if (e.key === '-' || e.key === 'Minus') {
          e.preventDefault();
          return;
        }
      }

      // Allow minus sign only at the start for negative numbers
      if (e.key === '-' && currentValue.length === 0) {
        // Call the original onKeyDown if provided
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // Allow decimal point only once
      if (e.key === '.' || e.key === ',') {
        if (currentValue.includes('.') || currentValue.includes(',')) {
          e.preventDefault();
          return;
        }
        // Call the original onKeyDown if provided
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
        return;
      }

      // Only allow numbers (0-9)
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        return;
      }
    }

    // Call the original onKeyDown if provided
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  const inputProps = {
    ...props,
    onChange: handleInputChange,
    onKeyDown: (props.type === 'number' || props.type === 'tel' || props.type === 'phone' || props.pattern) ? handleNumberKeyDown : props.onKeyDown,
  };

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label} {(props.required || showAsterik) && <span className="text-red-600">*</span>} 
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className="text-gray-400 text-sm">{leftIcon}</div>
          </div>
        )}

        <input
          id={inputId}
          readOnly={readOnly}
          className={cn(
            'flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50',
            // Conditional focus styles - only apply if not readonly
            !readOnly &&
              'focus:outline-none focus:ring-0 focus:border focus:border-primary-600',
            // Readonly styles
            readOnly && 'bg-gray-100  focus:outline-none focus:ring-0 focus:border-gray-200 !cursor-not-allowed',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            (error || validationError) && 'border-error focus:ring-error',
            className
          )}
          style={{
            boxShadow: '0px 1px 2px 0px #1018280d',
            ...(readOnly && {
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              cursor: 'default',
            }),
            ...inputProps.style,
          }}
          //prevent user selection (disabling mouse events)
          onMouseDown={readOnly ? e => e.preventDefault() : undefined}
          {...inputProps}
          inputMode={props.type === 'number' ? 'numeric' : undefined}
          pattern={props.type === 'number' ? '[0-9]*' : props.pattern}
        />
        {showRemoveIcon && inputValue && (
          <div
            className="absolute inset-y-0 right-2 pr-3 flex items-center"
            onClick={() => {
              // Clear the input value on remove icon click
              const event = {
                target: {
                  value: '',
                  name: props.name,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleInputChange(event);
            }}
            style={{ cursor: 'pointer' }}
          >
            <CircleX className="text-gray-400" size={20} />
          </div>
        )}
        {/* Right icon for actions like showing/hiding password */}
        {rightIcon && (
          <div
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={onRightIconClick}
            style={{ cursor: onRightIconClick ? 'pointer' : 'default' }}
          >
            <div className="text-gray-400 text-sm">{rightIcon}</div>
          </div>
        )}
      </div>

      {(error || validationError) && (
        <p className="mt-1 text-sm text-red-600">{error || validationError}</p>
      )}

      {helpText && !error && !validationError && (
        <p className="mt-1 text-sm text-gray-500">{helpText}</p>
      )}
    </div>
  );
};
